/**
 * Screenshot URL helpers: normalize Firestore activity payloads and pick
 * preview (thumbnail) vs full-resolution URLs for UI.
 *
 * Activity shape from API:
 * - `screenshots: string[]` — full-resolution URLs
 * - `screenshotThumbs?: string[]` — parallel thumbnails (same indexes)
 */

/**
 * Normalized row for one screenshot frame (lists + modals).
 * @typedef {{ url: string, thumbUrl?: string, timestamp?: unknown, activityId?: string, screenIndex: number }} ScreenshotRow
 */

/**
 * @param {Record<string, unknown> | null | undefined} activity
 * @returns {ScreenshotRow[]}
 */
export function flattenScreenshotsFromActivity(activity) {
  if (!activity) return [];
  const full = Array.isArray(activity.screenshots) ? activity.screenshots : [];
  const thumbs = Array.isArray(activity.screenshotThumbs)
    ? activity.screenshotThumbs
    : [];

  return full.map((url, idx) => {
    const fullUrl = typeof url === 'string' ? url : '';
    const rawThumb = thumbs[idx];
    const thumbUrl =
      typeof rawThumb === 'string' && rawThumb.trim() ? rawThumb.trim() : '';
    return {
      url: fullUrl,
      thumbUrl,
      timestamp: activity.timestamp ?? null,
      activityId: activity.id,
      screenIndex: idx,
    };
  });
}

/**
 * Flatten all screenshots from session activities (full + optional thumbs).
 * @param {Record<string, unknown>[] | null | undefined} activities
 * @returns {ScreenshotRow[]}
 */
export function collectScreenshotsFromActivities(activities) {
  const out = [];
  (activities || []).forEach((activity) => {
    flattenScreenshotsFromActivity(activity).forEach((row) => {
      if (row.url) out.push(row);
    });
  });
  return out;
}

/**
 * Grid / strip / modal filmstrip: prefer dedicated thumb when present.
 * @param {Partial<ScreenshotRow> | null | undefined} shot
 * @returns {string}
 */
export function getScreenshotPreviewSrc(shot) {
  if (!shot) return '';
  if (typeof shot.thumbUrl === 'string' && shot.thumbUrl.trim()) {
    return shot.thumbUrl.trim();
  }
  return typeof shot.url === 'string' ? shot.url : '';
}

/**
 * Modal main view / “open original”: always full-resolution URL.
 * @param {Partial<ScreenshotRow> | null | undefined} shot
 * @returns {string}
 */
export function getScreenshotFullSrc(shot) {
  if (!shot) return '';
  return typeof shot.url === 'string' ? shot.url : '';
}

export function getScreenshotRevealKey(screenshot) {
  if (!screenshot) return '';
  const id = screenshot.activityId != null ? screenshot.activityId : 'na';
  const idx = screenshot.screenIndex != null ? screenshot.screenIndex : 0;
  const url = screenshot.url || '';
  return `${id}:${idx}:${url}`;
}
